import Box from "@/elements/Box";
import NumberInput from "@/elements/Input/NumberInput";
import {
  FormHelperText,
  Grid,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import Card from "@mui/material/Card";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
} from "react-hook-form";
import { RegistroDto, UpdateRegistroDto } from "@/api";
import { isNil, keyBy } from "lodash";
import { useTranslation } from "react-i18next";

export type Registri_GFL_MONITORING_WTP = {
  measHumTh1?: number;
  measHumTh2?: number;

  measPartTh1?: number;
  measPartTh2?: number;
};

export function registriToForm_GFL_MONITORING_WTP(_registri: RegistroDto[]): {
  [r in keyof Registri_GFL_MONITORING_WTP]: any;
} {
  const registri = keyBy(_registri, "registro");

  return {
    measHumTh1: isNil(registri["measHumTh1"])
      ? undefined
      : parseFloat(registri["measHumTh1"].value),
    measHumTh2: isNil(registri["measHumTh2"])
      ? undefined
      : parseFloat(registri["measHumTh2"].value),

    measPartTh1: isNil(registri["measPartTh1"])
      ? undefined
      : parseFloat(registri["measPartTh1"].value),
    measPartTh2: isNil(registri["measPartTh2"])
      ? undefined
      : parseFloat(registri["measPartTh2"].value),
  };
}

export function formToRegistri_GFL_MONITORING_WTP(
  form: Registri_GFL_MONITORING_WTP
): UpdateRegistroDto[] {
  const topicPrefix = "var";
  const _values: {
    [k in keyof Registri_GFL_MONITORING_WTP]?: UpdateRegistroDto;
  } = {
    measHumTh1: isNil(form.measHumTh1)
      ? undefined
      : {
          registro: "measHumTh1",
          topicPrefix,
          value: form.measHumTh1,
        },
    measHumTh2: isNil(form.measHumTh2)
      ? undefined
      : {
          registro: "measHumTh2",
          topicPrefix,
          value: form.measHumTh2,
        },

    measPartTh1: isNil(form.measPartTh1)
      ? undefined
      : {
          registro: "measPartTh1",
          topicPrefix,
          value: form.measPartTh1,
        },
    measPartTh2: isNil(form.measPartTh2)
      ? undefined
      : {
          registro: "measPartTh2",
          topicPrefix,
          value: form.measPartTh2,
        },
  };
  return Object.values(_values);
}

export function EditorRegistri_GFL_MONITORING_WTP({
  control,
  errors,
  register,
  getValues,
  readOnly = false,
}: {
  control: Control<Registri_GFL_MONITORING_WTP>;
  errors: FieldErrors<Registri_GFL_MONITORING_WTP>;
  register: UseFormRegister<Registri_GFL_MONITORING_WTP>;
  getValues: UseFormGetValues<Registri_GFL_MONITORING_WTP>;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Card>
        <Box p={2} flex={1}>
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography
              variant="h5"
              color="text"
              fontWeight="medium"
              gutterBottom
            >
              {t("gflMonitoringWtp.titolo")}
            </Typography>

            <Grid container rowSpacing={1} columnSpacing={2}>
              <Grid item sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                  >
                    {t("gflMonitoringWtp.campi.umiditaSoglia1")}
                  </Typography>
                  <Controller
                    control={control}
                    name="measHumTh1"
                    rules={{
                      min: {
                        value: 0,
                        message: t("gflMonitoringWtp.errori.rangePercentuale", {
                          min: 0,
                          max: 100,
                        }),
                      },
                      max: {
                        value: 100,
                        message: t("gflMonitoringWtp.errori.rangePercentuale", {
                          min: 0,
                          max: 100,
                        }),
                      },
                      validate: (measHumTh1: number | undefined) => {
                        const { measHumTh2 } = getValues();
                        if (
                          measHumTh1 &&
                          measHumTh2 &&
                          measHumTh1 > measHumTh2
                        ) {
                          return t("gflMonitoringWtp.errori.soglia1SuperaSoglia2");
                        }
                        return true;
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 100 }}
                          endAdornment={
                            <InputAdornment position="end">%</InputAdornment>
                          }
                          numericInputProps={{ decimalScale: 0 }}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item sm={12} md={6} />

              <Grid item sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                  >
                    {t("gflMonitoringWtp.campi.umiditaSoglia2")}
                  </Typography>
                  <Controller
                    control={control}
                    name="measHumTh2"
                    rules={{
                      min: {
                        value: 0,
                        message: t("gflMonitoringWtp.errori.rangePercentuale", {
                          min: 0,
                          max: 100,
                        }),
                      },
                      max: {
                        value: 100,
                        message: t("gflMonitoringWtp.errori.rangePercentuale", {
                          min: 0,
                          max: 100,
                        }),
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 100 }}
                          endAdornment={
                            <InputAdornment position="end">%</InputAdornment>
                          }
                          numericInputProps={{
                            decimalScale: 0,
                            min: 0,
                            max: 100,
                          }}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item sm={12} md={6} />

              <Grid item sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                  >
                    {t("gflMonitoringWtp.campi.particelleIso4Soglia1")}
                  </Typography>
                  <Controller
                    control={control}
                    name="measPartTh1"
                    rules={{
                      min: {
                        value: 10,
                        message: t("gflMonitoringWtp.errori.rangeParticelle", {
                          min: 10,
                          max: 30,
                        }),
                      },
                      max: {
                        value: 30,
                        message: t("gflMonitoringWtp.errori.rangeParticelle", {
                          min: 10,
                          max: 30,
                        }),
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 100 }}
                          numericInputProps={{
                            decimalScale: 0,
                          }}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item sm={12} md={6} />

              <Grid item sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                  >
                    {t("gflMonitoringWtp.campi.particelleIso4Soglia2")}
                  </Typography>
                  <Controller
                    control={control}
                    name="measPartTh2"
                    rules={{
                      min: {
                        value: 10,
                        message: t("gflMonitoringWtp.errori.rangeParticelle", {
                          min: 10,
                          max: 30,
                        }),
                      },
                      max: {
                        value: 30,
                        message: t("gflMonitoringWtp.errori.rangeParticelle", {
                          min: 10,
                          max: 30,
                        }),
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 100 }}
                          numericInputProps={{
                            decimalScale: 0,
                          }}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item sm={12} md={6} />
            </Grid>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}
